import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
    CalendarDays,
    User,
    MapPin,
    Globe,
    Pencil,
    Share2,
    Info,
    Users,
} from 'lucide-react';
import { useMember, useDeleteMember, useUpdateMemberStatus } from '@/lib/api/hooks/member';
import { ActionDropdown, DeleteModal, PublishModal, UnpublishModal, type DropdownAction } from '@/components/ui';
import { showToast } from '@/lib/toast';
import type { UpdateMember } from '@/lib/api/types';

const MemberDetailPage: React.FC = () => {
    const { memberId } = useParams<{ memberId: string }>();
    const navigate = useNavigate();

    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [statusModalOpen, setStatusModalOpen] = useState(false);

    const { data: memberData, isLoading, error } = useMember(memberId || '');
    const member = memberData?.data;

    const { mutate: deleteMember, isPending: isDeleting } = useDeleteMember();
    const { mutate: updateStatus, isPending: isUpdatingStatus } = useUpdateMemberStatus();

    if (!memberId) {
        return (
            <div className="text-center p-8 bg-white rounded-lg">
                <h2 className="text-xl font-bold">Member ID missing</h2>
                <button onClick={() => navigate('/admin/members')} className="mt-4 text-[#681628] hover:underline">
                    &larr; Back to Leadership
                </button>
            </div>
        );
    }

    const formatDate = (dateString?: string | null) => {
        if (!dateString) return '-';
        try {
            const date = new Date(dateString);
            if (Number.isNaN(date.getTime())) return '-';
            return date.toLocaleDateString('en-US', {
                day: 'numeric',
                month: 'short',
                year: undefined
            }) + ' at ' + date.toLocaleTimeString('en-US', {
                hour: 'numeric',
                minute: '2-digit',
                hour12: true
            });
        } catch {
            return '-';
        }
    };

    // const formatDateShort = (dateString?: string | null) => {
    //     if (!dateString) return '-';
    //     try {
    //         const date = new Date(dateString);
    //         if (Number.isNaN(date.getTime())) return '-';
    //         return date.toLocaleDateString('en-US', {
    //             day: 'numeric',
    //             month: 'short',
    //             year: 'numeric'
    //         });
    //     } catch {
    //         return '-';
    //     }
    // };

    const formatCategory = (category: string) => {
        switch (category) {
            case 'PRESIDENT':
                return 'President';
            case 'VICE_PRESIDENT':
                return 'Vice President';
            case 'DIRECTOR':
                return 'Director';
            case 'SECRETARY':
                return 'Secretary';
            case 'MEMBER':
                return 'Member';
            default:
                return category.charAt(0) + category.slice(1).toLowerCase().replace(/_/g, ' ');
        }
    };

    const getCategoryTabLabel = (category: string) => {
        if (category === 'PRESIDENT' || category === 'VICE_PRESIDENT') {
            return 'Presidential Council';
        }
        return 'Members';
    };

    const handleEdit = () => {
        navigate(`/admin/members/edit/${memberId}`, {
            state: { member }
        });
    };

    const handleDeleteClick = () => {
        setDeleteModalOpen(true);
    };

    const handleStatusUpdateClick = () => {
        if (!member) return;
        setStatusModalOpen(true);
    };

    const confirmDelete = () => {
        if (!member) return;

        deleteMember(
            { memberId, data: {} as unknown as UpdateMember },
            {
                onSuccess: (response) => {
                    if (response.success) {
                        showToast.success('Member deleted successfully!');
                        setDeleteModalOpen(false);
                        navigate('/admin/members');
                    }
                },
                onError: (error: Error) => {
                    showToast.error('Failed to delete: ' + (error.message || 'Unknown error'));
                }
            }
        );
    };

    const confirmStatusUpdate = () => {
        if (!member) return;
        const newStatus = member.status === 'PUBLISH' ? 'DRAFT' : 'PUBLISH';

        updateStatus(
            { memberId, data: { status: newStatus } },
            {
                onSuccess: (response) => {
                    if (response.success) {
                        showToast.success(
                            newStatus === 'PUBLISH'
                                ? 'Member published successfully!'
                                : 'Member unpublished successfully!'
                        );
                        setStatusModalOpen(false);
                    }
                },
                onError: (error: Error) => {
                    showToast.error('Failed to update status: ' + (error.message || 'Unknown error'));
                }
            }
        );
    };

    const getDropdownActions = (): DropdownAction[] => {
        const actions: DropdownAction[] = [
            {
                label: 'Edit',
                onClick: (e) => {
                    e.stopPropagation();
                    handleEdit();
                }
            },
            {
                label: member?.status === 'PUBLISH' ? 'Unpublish' : 'Publish',
                onClick: (e) => {
                    e.stopPropagation();
                    handleStatusUpdateClick();
                },
                disabled: isUpdatingStatus
            }
        ];

        if (member?.status === 'DRAFT') {
            actions.push({
                label: 'Delete',
                onClick: (e) => {
                    e.stopPropagation();
                    handleDeleteClick();
                },
                variant: 'danger' as const,
                disabled: isDeleting
            });
        }

        return actions;
    };

    // Parse social links from possible payload variants
    const getSocialLinks = () => {
        const rawLinks = (member as unknown as { socialMediaLinks?: unknown; socialLinks?: unknown }).socialMediaLinks
            || (member as unknown as { socialLinks?: unknown }).socialLinks;
        if (!rawLinks) return [];
        try {
            if (typeof rawLinks === 'string') {
                return JSON.parse(rawLinks);
            }
            return Array.isArray(rawLinks) ? rawLinks : [];
        } catch {
            return [];
        }
    };

    const getSocialIcon = (platform: string) => {
        switch (true) {
            case platform.toLowerCase().includes('linkedin'):
                return (
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
                    </svg>
                );
            case platform.toLowerCase().includes('twitter'):
                return (
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                    </svg>
                );
            case platform.toLowerCase().includes('facebook'):
                return (
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                    </svg>
                );
            default:
                return (
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                    </svg>
                );
        }
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#681628] mx-auto"></div>
                    <p className="mt-4 text-gray-600">Loading member...</p>
                </div>
            </div>
        );
    }

    if (error || !member) {
        return (
            <div className="text-center p-8 bg-white rounded-lg">
                <h2 className="text-xl font-bold text-red-600">Error loading member</h2>
                <p className="mt-2 text-gray-600">{error?.message || 'Member not found'}</p>
                <button onClick={() => navigate('/admin/members')} className="mt-4 text-[#681628] hover:underline">
                    &larr; Back to Leadership
                </button>
            </div>
        );
    }

    const socialLinks = getSocialLinks();
    const createdByName = (member as unknown as { createdUsername?: string }).createdUsername || member.createdByName || 'Admin';
    const roleLabel = formatCategory(member.memberCategory);
    const isPublished = member.status === 'PUBLISH';
    const statusLabel = isPublished ? 'Published' : 'Draft';
    const statusClass = isPublished
        ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
        : 'bg-blue-50 text-blue-700 border-blue-200';
    const categoryPillClass = member.memberCategory === 'PRESIDENT' || member.memberCategory === 'VICE_PRESIDENT'
        ? 'bg-wine-100 text-white border-wine-100'
        : 'bg-emerald-50 text-emerald-700 border-emerald-200';
    const memberSinceYear = member.createdAt ? new Date(member.createdAt).getFullYear() : 'N/A';

    const handleShare = async () => {
        try {
            await navigator.clipboard.writeText(window.location.href);
            showToast.success('Profile link copied successfully!');
        } catch {
            showToast.error('Unable to copy link.');
        }
    };

    return (
        <>
            <div className="space-y-6">
                {/* Breadcrumb */}
                <div className="flex items-center gap-2 text-sm">
                    <Link to="/admin/members" className="text-forestGreen-100 hover:underline font-medium">
                        Leadership
                    </Link>
                    <span className="text-gray-400">▸</span>
                    <span className="text-gray-600">{getCategoryTabLabel(member.memberCategory)}</span>
                </div>

                <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_300px] gap-6 items-start">
                    <div className="space-y-6">
                        {/* Hero Card */}
                        <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6 space-y-5">
                            <div className="flex items-start gap-5">
                                <div className="h-40 w-40 rounded-full overflow-hidden bg-gray-100 border border-gray-200">
                                    {member.imageUrl ? (
                                        <img
                                            src={member.imageUrl}
                                            alt={member.fullName}
                                            className="w-full h-full object-cover"
                                        />
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center bg-gray-200">
                                            <span className="text-3xl font-bold text-gray-400">
                                                {member.fullName.charAt(0)}
                                            </span>
                                        </div>
                                    )}
                                </div>

                                <div className=" flex flex-col gap-4">
                                    <div className="flex flex-wrap items-center gap-2">
                                      
                                        <span className={`inline-flex items-center rounded-md border px-3 py-1 text-xs font-medium ${categoryPillClass}`}>
                                            {roleLabel}
                                        </span>
                                          <span className={`inline-flex items-center rounded-md border px-3 py-1 text-xs font-medium ${statusClass}`}>
                                            {statusLabel}
                                        </span>
                                    </div>

                                    <h1 className="text-3xl font-semibold text-gray-900 leading-tight break-words">{member.fullName}</h1>

                                    <p className="text-lg inline-flex items-center gap-2 break-words">
                                        <MapPin className="w-5 h-5 text-gray-400" />
                                        {member.countryName || 'N/A'}
                                    </p>

                                    <div className="text-sm text-gray-500 flex whitespace-nowrap items-center gap-2">
                                        <span className="inline-flex items-center gap-1.5"><CalendarDays className="w-4 h-4" />Posted {formatDate(member.createdAt)}</span>
                                        <span className="text-gray-300">|</span>
                                        <span className="inline-flex items-center gap-1.5"><User className="w-4 h-4" />Posted by {createdByName}</span>
                                    </div>
                                </div>

                                <ActionDropdown
                                    id={`member-${memberId}`}
                                    actions={getDropdownActions()}
                                />
                            </div>

                         
                        </div>

                        {/* Biography */}
                        <div className="bg-white border border-gray-200 rounded-md shadow-sm p-6">
                            <div className="flex items-center gap-3 mb-4">
                                <div className='flex items-center justify-center h-10 w-10 rounded bg-[#951f3930]'>
<User color='#951f39' />
                                </div>
                            <h2 className="text-lg font-semibold text-gray-900">  Biography</h2>

                            </div>
                            <div
                                className="prose max-w-none text-gray-700"
                                dangerouslySetInnerHTML={{ __html: member.bio || 'No biography available.' }}
                            />
                        </div>

                        {/* Key Info */}
                        <div className="bg-white border border-gray-200 rounded-md shadow-sm p-6 flex flex-col gap-2">
                                <div className="flex items-center gap-3 mb-4">
                                <div className='flex items-center justify-center h-10 w-10 rounded bg-[#1f549530]'>
<Info color='#1f5495' />
                                </div>
                            <h2 className="text-lg font-semibold text-gray-900">  Key Information</h2>

                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div className=" grid gap-3 grid-cols-[auto_1fr] border-gray-200 border rounded-md p-4">
                                    <CalendarDays strokeWidth={2} className="w-6 h-6" />
                                    <div className='flex flex-col gap-1'>
                                         <p className="text-sm text-gray-500">Member Since</p>
                                    <p className="text-lg font-semibold text-gray-900">{memberSinceYear}</p>
                                    </div>
                                   
                                </div>
                              <div className=" grid gap-3 grid-cols-[auto_1fr] border-gray-200 border rounded-md p-4">
                                <Users className="w-6 h-6" />

                                <div className='flex flex-col gap-1'>
  <p className="text-sm text-gray-500 ">Type</p>
                                    <p className="text-lg font-semibold text-gray-900">Individual</p>
                                </div>
                                  
                                </div>
                              <div className=" grid gap-3 grid-cols-[auto_1fr] border-gray-200 border rounded-md p-4">
                                <Globe className="w-6 h-6" />
                                <div className='flex flex-col gap-1'>
                                    <p className="text-sm text-gray-500 ">Visibility</p>
                                    <p className="text-lg font-semibold text-gray-900">{statusLabel}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <aside className="space-y-4 xl:sticky xl:top-24">
                        <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-5">
                            <h3 className="text-2xl font-semibold text-gray-900 mb-4">Profile Details</h3>
                            <div className="space-y-3 text-sm">
                                <div className="flex items-center justify-between gap-4">
                                    <span className="text-gray-500">Role</span>
                                    <span className={`inline-flex items-center rounded-md border px-2.5 py-1 text-xs font-medium ${categoryPillClass}`}>{roleLabel}</span>
                                </div>
                                <div className="flex items-center justify-between gap-4">
                                    <span className="text-gray-500">Status</span>
                                    <span className={`inline-flex items-center rounded-md border px-2.5 py-1 text-xs font-medium ${statusClass}`}>{statusLabel}</span>
                                </div>
                                <div className="flex items-center justify-between gap-4">
                                    <span className="text-gray-500">Published On</span>
                                    <span className="text-gray-900 font-medium text-right">{formatDate(member.datePublished)}</span>
                                </div>
                                <div className="flex items-center justify-between gap-4">
                                    <span className="text-gray-500">Last Updated</span>
                                    <span className="text-gray-900 font-medium text-right">{formatDate(member.updatedAt)}</span>
                                </div>
                                <div className="flex items-center justify-between gap-4">
                                    <span className="text-gray-500">Country</span>
                                    <span className="text-gray-900 font-medium text-right">{member.countryName || 'N/A'}</span>
                                </div>
                                {/* <div className="flex items-center justify-between gap-4">
                                    <span className="text-gray-500">Languages</span>
                                    <span className="text-gray-900 font-medium text-right">English</span>
                                </div> */}
                                <div className="flex items-center justify-between gap-4">
                                    <span className="text-gray-500">Category</span>
                                    <span className="text-gray-900 font-medium text-right">{getCategoryTabLabel(member.memberCategory)}</span>
                                </div>
                            </div>

                            <div className="mt-6 border-t border-gray-200 pt-4">
                                <p className="text-sm font-semibold text-gray-900 mb-3">Social & Contact Links</p>
                                <div className="flex items-center gap-2 flex-wrap">
                                    {socialLinks.length > 0 ? (
                                        socialLinks.map((link: string, index: number) => (
                                            <a
                                                key={index}
                                                href={link}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="h-10 w-10 rounded-md border border-gray-200 inline-flex items-center justify-center text-gray-600 hover:bg-gray-50"
                                                title={link}
                                            >
                                                {getSocialIcon(link)}
                                            </a>
                                        ))
                                    ) : (
                                        <>
                                            <span className="h-10 w-10 rounded-md border border-gray-200 inline-flex items-center justify-center text-gray-400"><Globe className="w-4 h-4" /></span>
                                            <span className="h-10 w-10 rounded-md border border-gray-200 inline-flex items-center justify-center text-gray-400"><User className="w-4 h-4" /></span>
                                            <span className="h-10 w-10 rounded-md border border-gray-200 inline-flex items-center justify-center text-gray-400"><Share2 className="w-4 h-4" /></span>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* No Content Health section as requested */}
                        <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-5">
                            <h3 className="text-2xl font-semibold text-gray-900 mb-4">Quick Actions</h3>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                       
                                <button
                                    type="button"
                                    onClick={handleEdit}
                                    className="inline-flex items-center justify-center gap-2 rounded-md border border-gray-200 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                                >
                                    <Pencil className="w-4 h-4" /> Edit Profile
                                </button>
                                <button
                                    type="button"
                                    onClick={handleShare}
                                    className="inline-flex items-center justify-center gap-2 rounded-md border border-gray-200 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                                >
                                    <Share2 className="w-4 h-4" /> Share
                                </button>
                        
                       
                            </div>
                        </div>
                    </aside>
                </div>
            </div>

            {/* Delete Modal */}
            <DeleteModal
                isOpen={deleteModalOpen}
                onClose={() => setDeleteModalOpen(false)}
                onConfirm={confirmDelete}
                title="Delete Member"
                subtitle="Are you sure you want to delete this member?"
                warningText="This action cannot be undone."
                isLoading={isDeleting}
                itemType="Member"
            />

            {/* Publish/Unpublish Modals */}
            {member.status === 'DRAFT' ? (
                <PublishModal
                    isOpen={statusModalOpen}
                    onClose={() => setStatusModalOpen(false)}
                    onConfirm={confirmStatusUpdate}
                    title="Publish Member"
                    subtitle="Are you sure you want to publish this member?"
                    itemType="Member"
                    isLoading={isUpdatingStatus}
                />
            ) : (
                <UnpublishModal
                    isOpen={statusModalOpen}
                    onClose={() => setStatusModalOpen(false)}
                    onConfirm={confirmStatusUpdate}
                    itemType="Member"
                    isLoading={isUpdatingStatus}
                />
            )}
        </>
    );
};

export default MemberDetailPage;
